import React, { useState } from 'react';
import { Table, Badge, Row, Col, Pagination } from 'react-bootstrap';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { format } from 'date-fns';
import { FaUsers, FaCheckCircle, FaExclamationTriangle, FaChevronDown, FaChevronUp, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { ReportData, DetailedRecord, PaginationResponse } from '../types/report.types';
import styles from './Reports.module.css';
import { useAppDispatch } from '../redux/store';
import { setSort } from '../redux/slices/reportSlice';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

interface ReportContentProps {
    report: ReportData;
    pagination: PaginationResponse;
    onPageChange: (page: number) => void;
    onItemsPerPageChange: (perPage: number) => void;
}

export const ReportContent: React.FC<ReportContentProps> = ({ 
    report, 
    pagination,
    onPageChange,
    onItemsPerPageChange
}) => {
    const dispatch = useAppDispatch();
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
    const [expandedRequestData, setExpandedRequestData] = useState<Set<string>>(new Set());
    const { loading } = useSelector((state: RootState) => state.reports);

    const handleSort = (field: string) => {
        // Close all expanded rows when sorting changes
        setExpandedRows(new Set());
        
        const currentSortField = pagination.sort_field;
        const currentSortDirection = pagination.sort_direction;
        
        let newDirection: 'asc' | 'desc' = 'asc';
        if (currentSortField === field) {
            newDirection = currentSortDirection === 'asc' ? 'desc' : 'asc';
        }
        
        dispatch(setSort({ field, direction: newDirection }));
    };

    const handlePageChange = (newPage: number) => {
        // Close all expanded rows when page changes
        setExpandedRows(new Set());
        onPageChange(newPage);
    };

    const handleItemsPerPageChange = (newPerPage: number) => {
        // Close all expanded rows when items per page changes
        setExpandedRows(new Set());
        onItemsPerPageChange(newPerPage);
    };

    const getSortIcon = (field: string) => {
        if (pagination.sort_field !== field) {
            return <FaSort className={styles.sortIcon} />;
        }
        return pagination.sort_direction === 'asc' ? 
            <FaSortUp className={styles.sortIcon} /> : 
            <FaSortDown className={styles.sortIcon} />;
    };

    const toggleRow = (id: string, event: React.MouseEvent) => {
        event.stopPropagation();
        const newExpandedRows = new Set(expandedRows);
        if (expandedRows.has(id)) {
            newExpandedRows.delete(id);
        } else {
            newExpandedRows.add(id);
        }
        setExpandedRows(newExpandedRows);
    };

    const toggleRequestData = (id: string, event: React.MouseEvent) => {
        event.stopPropagation();
        const newExpandedRequestData = new Set(expandedRequestData);
        if (expandedRequestData.has(id)) {
            newExpandedRequestData.delete(id);
        } else {
            newExpandedRequestData.add(id);
        }
        setExpandedRequestData(newExpandedRequestData);
    };

    const renderPagination = () => {
        const { page, total_pages, total_items, per_page } = pagination;
        
        if (total_items <= per_page) return null;

        return (
            <div className={styles.paginationContainer}>
                <div className={styles.recordsInfo}>
                    Showing {((page - 1) * per_page) + 1} to {Math.min(page * per_page, total_items)} of {total_items} records
                </div>
                <div className={styles.paginationControls}>
                    <select
                        value={per_page}
                        onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                        className={styles.itemsPerPage}
                    >
                        <option value={10}>10 per page</option>
                        <option value={20}>20 per page</option>
                        <option value={30}>30 per page</option>
                        <option value={50}>50 per page</option>
                    </select>
                    <Pagination>
                        <Pagination.First
                            disabled={page === 1}
                            onClick={() => handlePageChange(1)}
                        />
                        <Pagination.Prev
                            disabled={page === 1}
                            onClick={() => handlePageChange(page - 1)}
                        />
                        {Array.from({ length: total_pages }, (_, i) => i + 1)
                            .filter(p => Math.abs(p - page) <= 2 || p === 1 || p === total_pages)
                            .map((p, i, arr) => {
                                if (i > 0 && arr[i - 1] !== p - 1) {
                                    return [
                                        <Pagination.Ellipsis key={`ellipsis-${p}`} />,
                                        <Pagination.Item
                                            key={p}
                                            active={p === page}
                                            onClick={() => handlePageChange(p)}
                                        >
                                            {p}
                                        </Pagination.Item>
                                    ];
                                }
                                return (
                                    <Pagination.Item
                                        key={p}
                                        active={p === page}
                                        onClick={() => handlePageChange(p)}
                                    >
                                        {p}
                                    </Pagination.Item>
                                );
                            })}
                        <Pagination.Next
                            disabled={page === total_pages}
                            onClick={() => handlePageChange(page + 1)}
                        />
                        <Pagination.Last
                            disabled={page === total_pages}
                            onClick={() => handlePageChange(total_pages)}
                        />
                    </Pagination>
                </div>
            </div>
        );
    };

    const generateUniqueKey = (record: DetailedRecord) => {
        return `${record.UnifiedEmployeeID}_${record.FileID}_${record.EMPLOYEE_ID}_${record.ProcessDate}`;
    };

    const renderDetailRow = (record: DetailedRecord, index: number) => {
        const uniqueKey = generateUniqueKey(record);
        const isExpanded = expandedRows.has(uniqueKey);
        
        const getStatusIndicator = (status: string, processFlag?: string) => {
            // Determine the display status
            let displayStatus = status;
            if (!status && processFlag === 'D') {
                displayStatus = 'Duplicate';
            }

            type StatusColorType = {
                processed: string;
                failed: string;
                pending: string;
                duplicate: string;
                [key: string]: string;
            };

            const statusColors: StatusColorType = {
                processed: '#198754',  // success green
                failed: '#dc3545',     // danger red
                pending: '#ffc107',    // warning yellow
                duplicate: '#6c757d'   // secondary gray for duplicates
            };
            
            const normalizedStatus = (displayStatus || '').toLowerCase();
            const backgroundColor = statusColors[normalizedStatus] || statusColors.pending;
            
            return (
                <div className={styles.statusContainer}>
                    <span 
                        className={styles.statusDot}
                        style={{ backgroundColor }}
                    />
                    <Badge bg={
                        displayStatus === 'Processed' ? 'success' :
                        displayStatus === 'Failed' ? 'danger' :
                        displayStatus === 'Duplicate' ? 'secondary' :
                        'warning'
                    }>
                        {displayStatus}
                    </Badge>
                </div>
            );
        };

        // Format name safely handling null values
        const formatName = (record: DetailedRecord) => {
            const firstName = record.FIRST_NAME || record.first_name || 'N/A';
            const lastName = record.LAST_NAME || record.last_name || '';
            return `${firstName} ${lastName}`.trim();
        };

        // Format date safely
        const formatProcessDate = (dateString: string) => {
            try {
                const date = new Date(dateString);
                if (isNaN(date.getTime())) {
                    return 'Invalid Date';
                }
                return format(date, 'MM/dd/yyyy HH:mm:ss');
            } catch (error) {
                return 'Invalid Date';
            }
        };
        
        return (
            <React.Fragment key={`${uniqueKey}_${index}`}>
                <tr 
                    className={styles.recordRow}
                    onClick={(event) => toggleRow(uniqueKey, event)}
                >
                    <td>
                        <div className={styles.cellContent}>
                            {record.EMPLOYEE_ID || 'N/A'}
                        </div>
                    </td>
                    <td>
                        <div className={styles.unifiedIdCell} title={record.UnifiedEmployeeID}>
                            {record.UnifiedEmployeeID}
                        </div>
                    </td>
                    <td>
                        <div className={styles.cellContent}>
                            {formatName(record)}
                        </div>
                    </td>
                    <td>
                        <div className={styles.fileNameCell} title={record.FileName}>
                            {record.FileName}
                        </div>
                    </td>
                    <td>
                        <div className={styles.cellContent}>
                            {record.FileID || 'N/A'}
                        </div>
                    </td>
                    <td>
                        {getStatusIndicator(record.process_status, record.PROCESS_FLAG)}
                    </td>
                    <td>
                        <div className={styles.cellContent}>
                            {record.Household_ID_SALES_LOGIX || 'N/A'}
                        </div>
                    </td>
                    <td className={styles.processDateCell}>
                        {formatProcessDate(record.ProcessDate)}
                    </td>
                    <td className="text-center">
                        {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                    </td>
                </tr>
                {isExpanded && (
                    <tr>
                        <td colSpan={8}>
                            <div className={styles.expandedContent}>
                                <div className={styles.section}>
                                    <h5>Enrollments</h5>
                                    <Table size="sm" className={styles.nestedTable}>
                                        <thead>
                                            <tr>
                                                <th>Plan Name</th>
                                                <th>Plan Code</th>
                                                <th>Coverage</th>
                                                <th>Start Date</th>
                                                <th>End Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {record.enrollments.map((enrollment, idx) => (
                                                <tr key={`${uniqueKey}_enrollment_${idx}`}>
                                                    <td className={styles.textLeft}>{enrollment.plan_name}</td>
                                                    <td>{enrollment.plan_code}</td>
                                                    <td>{enrollment.coverage || 'N/A'}</td>
                                                    <td>{enrollment.start_date}</td>
                                                    <td>{enrollment.end_date || 'N/A'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                                
                                <div className={styles.section}>
                                    <h5>Dependents</h5>
                                    <Table size="sm" className={styles.nestedTable}>
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Relationship</th>
                                                <th>DOB</th>
                                                <th>Gender</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Array.isArray(record.dependents) && record.dependents.map((dependent, idx) => {
                                                if (!dependent) return null;
                                                return (
                                                    <tr key={`${uniqueKey}_dependent_${idx}`}>
                                                        <td className={styles.textLeft}>
                                                            {`${dependent.first_name || 'N/A'} ${dependent.last_name || ''}`}
                                                        </td>
                                                        <td>{dependent.relationship || 'N/A'}</td>
                                                        <td>
                                                            {dependent.dob ? format(new Date(dependent.dob), 'MM/dd/yyyy') : 'N/A'}
                                                        </td>
                                                        <td>{dependent.gender || 'N/A'}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>

                                {/* Show Errors last if process_status is Failed or Validation Failed */}
                                {(record.process_status === 'Failed' || record.process_status === 'Validation Failed') && record.errors && (
                                    <div className={styles.section}>
                                        <h5>Errors</h5>
                                        <Table size="sm" className={styles.nestedTable}>
                                            <thead>
                                                <tr>
                                                    <th>Status</th>
                                                    <th>Operation</th>
                                                    <th>Date</th>
                                                    <th>Error Message</th>
                                                    <th>Request Data</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(() => {
                                                    try {
                                                        // Parse the errors string if it's not already an array
                                                        const parsedErrors = typeof record.errors === 'string' ? 
                                                            JSON.parse(record.errors) : record.errors;
                                                        
                                                        if (!Array.isArray(parsedErrors) || parsedErrors.length === 0) {
                                                            return (
                                                                <tr>
                                                                    <td colSpan={5} className={styles.textCenter}>
                                                                        No error details available
                                                                    </td>
                                                                </tr>
                                                            );
                                                        }

                                                        return parsedErrors.map((error, idx) => {
                                                            if (!error) return null;

                                                            // Parse request data if it exists and is a string
                                                            let formattedRequestData: string | JSX.Element = 'N/A';
                                                            const requestDataId = `${uniqueKey}_error_${idx}_request`;
                                                            const isRequestDataExpanded = expandedRequestData.has(requestDataId);
                                                            
                                                            try {
                                                                if (error.request_data && error.request_data !== 'null') {
                                                                    const parsedRequest = JSON.parse(error.request_data);
                                                                    formattedRequestData = (
                                                                        <div className={styles.requestDataContainer}>
                                                                            <div 
                                                                                className={styles.requestDataToggle}
                                                                                onClick={(e) => toggleRequestData(requestDataId, e)}
                                                                            >
                                                                                <span>View Request Data</span>
                                                                                {isRequestDataExpanded ? <FaChevronUp /> : <FaChevronDown />}
                                                                            </div>
                                                                            {isRequestDataExpanded && (
                                                                                <div className={styles.jsonData}>
                                                                                    <pre>
                                                                                        {JSON.stringify(parsedRequest, null, 2)}
                                                                                    </pre>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                }
                                                            } catch (e) {
                                                                formattedRequestData = error.request_data || 'N/A';
                                                            }

                                                            return (
                                                                <tr key={`${uniqueKey}_error_${idx}`}>
                                                                    <td>
                                                                        <Badge bg={error.status?.toLowerCase() === 'failed' ? 'danger' : 'warning'}>
                                                                            {error.status || 'Unknown'}
                                                                        </Badge>
                                                                    </td>
                                                                    <td>{error.operation || 'N/A'}</td>
                                                                    <td>
                                                                        {error.created_dt ? 
                                                                            format(new Date(error.created_dt), 'MM/dd/yyyy HH:mm:ss') : 
                                                                            'N/A'
                                                                        }
                                                                    </td>
                                                                    <td className={styles.textLeft}>{error.error_message || 'N/A'}</td>
                                                                    <td className={styles.requestData}>
                                                                        {formattedRequestData}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        });
                                                    } catch (e) {
                                                        console.error('Error parsing errors:', e);
                                                        return (
                                                            <tr>
                                                                <td colSpan={5} className={styles.textCenter}>
                                                                    Error parsing error details
                                                                </td>
                                                            </tr>
                                                        );
                                                    }
                                                })()}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </div>
                        </td>
                    </tr>
                )}
            </React.Fragment>
        );
    };

    const formatCellValue = (value: any) => {
        if (value === null || value === undefined) return '';
        
        // If it's a string that needs toLowerCase
        if (typeof value === 'string') {
            return value.toLowerCase();
        }
        
        // For other types
        return value.toString();
    };

    return (
        <div className={styles.reportContent}>
            <div className={styles.tableContainer}>
                {loading && (
                    <div className={`${styles.loadingOverlay} ${loading ? styles.visible : ''}`}>
                        <div className={styles.spinner}></div>
                    </div>
                )}
                <Table hover className={`${styles.recordsTable} ${loading ? styles.loading : ''}`}>
                    <thead>
                        <tr>
                            <th onClick={() => handleSort('EMPLOYEE_ID')} className={styles.sortableHeader}>
                                Employee ID {getSortIcon('EMPLOYEE_ID')}
                            </th>
                            <th onClick={() => handleSort('UnifiedEmployeeID')} className={styles.sortableHeader}>
                                Unified ID {getSortIcon('UnifiedEmployeeID')}
                            </th>
                            <th onClick={() => handleSort('FIRST_NAME')} className={styles.sortableHeader}>
                                Name {getSortIcon('FIRST_NAME')}
                            </th>
                            <th onClick={() => handleSort('FileName')} className={styles.sortableHeader}>
                                File Name {getSortIcon('FileName')}
                            </th>
                            <th onClick={() => handleSort('FileID')} className={styles.sortableHeader}>
                                File ID {getSortIcon('FileID')}
                            </th>
                            <th onClick={() => handleSort('process_status')} className={styles.sortableHeader}>
                                Status {getSortIcon('process_status')}
                            </th>
                            <th onClick={() => handleSort('Household_ID_SALES_LOGIX')} className={styles.sortableHeader}>
                                Household ID {getSortIcon('Household_ID_SALES_LOGIX')}
                            </th>
                            <th onClick={() => handleSort('ProcessDate')} className={styles.sortableHeader}>
                                Process Date {getSortIcon('ProcessDate')}
                            </th>
                            <th className={styles.actionHeader}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {report.detailed_records.map((record, index) => renderDetailRow(record, index))}
                    </tbody>
                </Table>
                {renderPagination()}
            </div>
        </div>
    );
}; 